import {
  VariableDesc,
  VariableType,
  variableTypeFromJSON,
} from '@/proto/common';

/** The canonical collect-variable shape shared by the agent API and compiler-only op. */
export interface CollectVariableDescriptorInput {
  variable_name?: unknown;
  name?: unknown;
  display_name?: unknown;
  description?: unknown;
  variable_type?: unknown;
  choices?: unknown;
  sample_value?: unknown;
  is_multi_select?: unknown;
}

export class CollectVariableDescriptorParseError extends Error {}

const VARIABLE_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

function optionalString(
  value: unknown,
  field: string,
  fallback: string
): string {
  if (value === undefined || value === null) return fallback;
  if (typeof value !== 'string') {
    throw new CollectVariableDescriptorParseError(
      `${field}: expected a string`
    );
  }
  return value;
}

/**
 * Decode agent collect metadata into the exact VariableDesc persisted on a workflow node.
 *
 * Agent-detail responses use `variable_name`; the trusted compiler op uses the protobuf-aligned
 * `name`. Keeping both paths here prevents their schema/type mappings from drifting apart.
 */
export function decodeCollectVariableDescriptors(
  raw: readonly CollectVariableDescriptorInput[],
  context = 'collect'
): VariableDesc[] {
  const names = new Set<string>();
  return raw.map((item, index) => {
    const itemContext = `${context}[${index}]`;
    const rawName = item.variable_name ?? item.name;
    if (typeof rawName !== 'string' || rawName.length === 0) {
      throw new CollectVariableDescriptorParseError(
        `${itemContext}.variable_name: expected a non-empty string`
      );
    }
    if (!VARIABLE_NAME_PATTERN.test(rawName)) {
      throw new CollectVariableDescriptorParseError(
        `${itemContext}.variable_name: invalid reference name`
      );
    }
    if (names.has(rawName)) {
      throw new CollectVariableDescriptorParseError(
        `${itemContext}.variable_name: duplicate ${rawName}`
      );
    }
    names.add(rawName);

    if (typeof item.variable_type !== 'string') {
      throw new CollectVariableDescriptorParseError(
        `${itemContext}.variable_type: expected a string`
      );
    }
    const type = variableTypeFromJSON(item.variable_type);
    if (type === VariableType.UNRECOGNIZED) {
      throw new CollectVariableDescriptorParseError(
        `${itemContext}.variable_type: unknown type ${item.variable_type}`
      );
    }

    const rawChoices = item.choices;
    if (rawChoices !== undefined && !Array.isArray(rawChoices)) {
      throw new CollectVariableDescriptorParseError(
        `${itemContext}.choices: expected an array`
      );
    }
    const choices = (Array.isArray(rawChoices) ? rawChoices : []).map(
      (choice, choiceIndex) => {
        if (typeof choice !== 'string') {
          throw new CollectVariableDescriptorParseError(
            `${itemContext}.choices[${choiceIndex}]: expected a string`
          );
        }
        return choice;
      }
    );
    if (
      item.is_multi_select !== undefined &&
      typeof item.is_multi_select !== 'boolean'
    ) {
      throw new CollectVariableDescriptorParseError(
        `${itemContext}.is_multi_select: expected a boolean`
      );
    }

    return VariableDesc.fromPartial({
      name: rawName,
      display_name: optionalString(
        item.display_name,
        `${itemContext}.display_name`,
        rawName
      ),
      type,
      description: optionalString(
        item.description,
        `${itemContext}.description`,
        ''
      ),
      choices,
      sample_value: optionalString(
        item.sample_value,
        `${itemContext}.sample_value`,
        ''
      ),
      is_multi_select:
        typeof item.is_multi_select === 'boolean'
          ? item.is_multi_select
          : false,
    });
  });
}
